<template>
    <div
        v-if="isContractApproved"
        class="invoice-upload-modal-wrapper"
    >
        <div class="invoice-upload-modal-wrapper__content mb-5">
            <CoreTwoColumnsRow>
                <template #left>
                    <CoreBorderWrapper
                        :noBorder="true"
                        class="flex-grow-1 flex-shrink-1 flex-basis-55"
                    >
                        {{ $t('§invoice.select_invoice_type') }}
                    </CoreBorderWrapper>
                </template>

                <template #right>
                    <CoreDropdown
                        v-model="invoice_type"
                        :options="invoiceTypes"
                        :sort="false"
                        :condensed="true"
                        :fullWidth="true"
                        :filter="false"
                        class="flex-grow-1 flex-shrink-1 flex-basis-45"
                    />
                </template>
            </CoreTwoColumnsRow>

            <CoreTwoColumnsRow>
                <template #left>
                    <CoreBorderWrapper
                        :noBorder="true"
                        class="flex-grow-1 flex-shrink-1 flex-basis-55"
                    >
                        {{ $t('§invoice.select_billing_entity') }}
                    </CoreBorderWrapper>
                </template>

                <template #right>
                    <CoreDropdown
                        v-model="billing_entity_id"
                        :options="billingEntityOptions"
                        :condensed="true"
                        :fullWidth="true"
                        :filter="false"
                        class="flex-grow-1 flex-shrink-1 flex-basis-45"
                    />
                </template>
            </CoreTwoColumnsRow>

            <CoreTwoColumnsRow>
                <template #left>
                    <CoreBorderWrapper
                        :noBorder="true"
                        class="flex-grow-1 flex-shrink-1 flex-basis-55"
                    >
                        {{ $t('§invoice.set_period_begin_and_period_end') }}
                    </CoreBorderWrapper>
                </template>

                <template #right>
                    <CoreDateInput
                        v-model="dates"
                        selectionMode="range"
                        :condensed="true"
                        :fullWidth="true"
                        class="flex-grow-1 flex-shrink-1 flex-basis-45"
                    />
                </template>
            </CoreTwoColumnsRow>

            <CoreTwoColumnsRow>
                <template #left>
                    <CoreBorderWrapper
                        :noBorder="true"
                        class="flex-grow-1 flex-shrink-1 flex-basis-55"
                    >
                        {{ $t('§invoice.amount') }}
                    </CoreBorderWrapper>
                </template>
                <template #right>
                    <CoreCurrencyInput
                        v-model="amount"
                        :condensed="true"
                        :maxFractionDigits="2"
                        class="flex-grow-1 flex-shrink-1 flex-basis-45"
                        :currency="contractCurrency"
                    />
                </template>
            </CoreTwoColumnsRow>

            <CoreTwoColumnsRow>
                <template #left>
                    <CoreBorderWrapper
                        :noBorder="true"
                        class="flex-grow-1 flex-shrink-1 flex-basis-55"
                    >
                        {{ $t('§invoice.is_paid') }}
                    </CoreBorderWrapper>
                </template>
                <template #right>
                    <CoreSelectButton
                        :options="isPaidOptions"
                        v-model="is_paid"
                        name="isPaid"
                    />
                </template>
            </CoreTwoColumnsRow>

            <CoreFileUpload
                accept=".pdf"
                class="mb-5"
                name="file"
                ref="fileUpload"
                :disabled="isFileSelectDisabled"
                :metadata="getUploadMetadata()"
                :uploadUrl="uploadUrl"
                @file-added="handleFileAdded"
                @uploaded="handleSuccess($event)"
            />
        </div>

        <div class="flex justify-start gap-2">
            <CoreButton
                label="§ui_button.upload"
                icon="pi-upload"
                :disabled="isUploadButtonDisabled"
                :loading="uploading"
                @click="handleUpload"
            />
            <CoreButton
                variant="tertiary"
                label="§ui_button.cancel"
                @click="handleClose"
                icon="pi-times"
            />
        </div>
    </div>
    <div
        v-else
        class="invoice-upload-modal-wrapper"
    >
        <p class="text-200 mb-6">
            {{ $t('§invoice.cannot_upload_invoice_as_contract_is_not_approved') }}
        </p>

        <div class="flex justify-start gap-2">
            <CoreButton
                variant="tertiary"
                @click="handleClose"
                label="§ui_button.cancel"
                icon="pi-times"
            />
        </div>
    </div>
</template>

<script>
import { format, isDate } from 'date-fns'
import InvoicesService from '@/api/InvoicesService.js'
import { useContractStore } from '@/stores/contract'
import { useModalStore } from '@/stores/modal'
import { useInvoicesStore } from '@/stores/invoices'
import CoreTwoColumnsRow from '@/components/core/CoreTwoColumnsRow.vue'
import CoreBorderWrapper from '@/components/core/CoreBorderWrapper.vue'
import CoreDropdown from '@/components/core/CoreDropdown.vue'
import CoreCheckbox from '@/components/core/CoreCheckbox.vue'
import CoreDateInput from '@/components/core/CoreDateInput.vue'
import CoreCurrencyInput from '@/components/core/CoreCurrencyInput.vue'
import CoreNumberInput from '@/components/core/CoreNumberInput.vue'
import CoreFileUpload from '@/components/core/CoreFileUpload.vue'
import CoreSelectButton from '@/components/core/CoreSelectButton.vue'
import CoreButton from '@/components/core/CoreButton.vue'

export default {
    name: 'InvoiceUploadModal',

    components: {
        CoreTwoColumnsRow,
        CoreBorderWrapper,
        CoreDropdown,
        CoreDateInput,
        CoreSelectButton,
        CoreCurrencyInput,
        CoreNumberInput,
        CoreFileUpload,
        CoreCheckbox,
        CoreButton,
    },

    data() {
        return {
            contractStore: useContractStore(),
            modalStore: useModalStore(),
            invoicesStore: useInvoicesStore(),

            isFileSelected: false,

            amount: null,
            invoice_type: '',
            is_paid: 0,
            dates: [],
            billing_entity_id: null,
            billingEntities: [],

            invoiceTypes: [
                { value: 'credit_note', label: 'Credit Note' },
                { value: 'custom', label: 'Custom' },
                { value: 'initial', label: 'Initial' },
                { value: 'second', label: 'Second' },
                { value: 'monthly', label: 'Monthly' },
                { value: 'anniversary', label: 'Anniversary' },
                { value: 'next_to_last', label: 'Next To Last' },
                { value: 'last', label: 'Last' },
            ],

            error: '',

            uploading: false,
        }
    },

    computed: {
        isFileSelectDisabled() {
            return (
                this.invoice_type === '' ||
                this.billing_entity_id === null ||
                !isDate(this.dates[0]) ||
                !isDate(this.dates[1]) ||
                this.uploading
            )
        },

        billingEntityOptions() {
            return this.billingEntities.map((entity) => ({
                value: entity.id,
                label: entity.name,
            }))
        },

        isAmountInvalid() {
            return this.amount === null || Number(this.amount) <= 0
        },

        isUploadButtonDisabled() {
            return !this.isFileSelected || this.isAmountInvalid || this.uploading
        },

        contract() {
            return this.contractStore.contractData.id
        },

        contractCurrency() {
            return this.contractStore.contractData.currency_code
        },

        isContractApproved() {
            const { contractData } = this.contractStore
            if (contractData.approved && Number(contractData.approved) === 1) {
                return true
            }

            return false
        },

        isPaidOptions() {
            return [
                { value: 0, label: this.$t('§invoice.no') },
                { value: 1, label: this.$t('§invoice.yes') },
            ]
        },

        uploadUrl() {
            return this.route('invoices.upload', [this.contractStore.contractId])
        },
    },

    methods: {
        handleFileAdded() {
            this.isFileSelected = true
        },

        formatDateForBackEnd(date) {
            if (!isDate(date)) {
                return ''
            }
            return format(date, 'yyyy-MM-dd')
        },

        handleClose() {
            this.modalStore.setClose()
        },

        getUploadMetadata() {
            return {
                invoice_type: this.invoice_type,
                is_paid: this.is_paid,
                period_begin: this.formatDateForBackEnd(this.dates[0]),
                period_end: this.formatDateForBackEnd(this.dates[1]),
                amount: Number(this.amount),
                billing_entity_id: this.billing_entity_id,
            }
        },

        async loadBillingEntities() {
            try {
                const { data } = await InvoicesService.getBillingEntities()
                this.billingEntities = data.billing_entities
            } catch (error) {
                console.error(
                    'Error fetching billing entities (InvoiceUploadModal/loadBillingEntities):',
                    error
                )
            }
        },

        handleUpload() {
            this.isFileSelected = false

            if (!this.uploading && this.$refs.fileUpload) {
                this.uploading = true
                this.$refs.fileUpload.upload()
            }
        },

        handleSuccess(e) {
            try {
                this.invoicesStore.invoicesTableRefreshKey += 1
                this.$toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: '§ui_message.invoice_upload_successful',
                    life: 1000,
                })
            } catch (error) {
                this.error = error.message || 'An error occurred while processing the file upload.'
                console.error(this.error)
                this.$toast.add({
                    severity: 'error',
                    detail: this.error,
                    life: 1000,
                })
            } finally {
                this.uploading = false
                this.isFileSelected = false
                this.handleClose()
            }
        },
    },

    mounted() {
        this.billing_entity_id = this.contractStore.contractData?.billing_entity_leasing_rates ?? null
        this.loadBillingEntities()
    },
}
</script>
